import React from "react";
import { motion } from "framer-motion";

// Skeleton animation variants
const skeletonVariants = {
  animate: {
    opacity: [0.5, 1, 0.5],
    transition: {
      duration: 1.5,
      repeat: Infinity,
      ease: "easeInOut",
    },
  },
};

// Base skeleton component
const Skeleton = ({ className = "", ...props }) => (
  <motion.div
    className={`bg-gray-200 rounded ${className}`}
    variants={skeletonVariants}
    animate="animate"
    {...props}
  />
);

// Product card skeleton
export const ProductCardSkeleton = () => (
  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden group hover:shadow-md transition-shadow duration-300">
    {/* Image skeleton */}
    <div className="relative aspect-square">
      <Skeleton className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200" />
      {/* Gradient overlay for hover effect */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
    </div>

    {/* Content skeleton */}
    <div className="p-4 space-y-3">
      {/* Title skeleton */}
      <Skeleton className="h-4 w-3/4 bg-gradient-to-r from-gray-200 to-gray-300" />

      {/* Price skeleton */}
      <Skeleton className="h-6 w-1/3 bg-gradient-to-r from-gray-200 to-gray-300" />

      {/* Rating skeleton */}
      <div className="flex items-center space-x-1">
        {[...Array(5)].map((_, i) => (
          <Skeleton
            key={i}
            className="w-4 h-4 rounded-full bg-gradient-to-r from-gray-200 to-gray-300"
          />
        ))}
        <Skeleton className="h-3 w-8 ml-2 bg-gradient-to-r from-gray-200 to-gray-300" />
      </div>

      {/* Button skeleton */}
      <Skeleton className="h-10 w-full rounded-lg bg-gradient-to-r from-gray-200 to-gray-300" />
    </div>
  </div>
);

// Text skeleton with multiple lines
export const TextSkeleton = ({ lines = 3, className = "" }) => (
  <div className={`space-y-2 ${className}`}>
    {[...Array(lines)].map((_, i) => (
      <Skeleton
        key={i}
        className={`h-4 ${i === lines - 1 ? "w-3/4" : "w-full"}`}
      />
    ))}
  </div>
);

// Image skeleton
export const ImageSkeleton = ({
  aspectRatio = "aspect-square",
  className = "",
}) => <Skeleton className={`w-full ${aspectRatio} rounded-lg ${className}`} />;

// Avatar skeleton
export const AvatarSkeleton = ({ size = "w-10 h-10", className = "" }) => (
  <Skeleton className={`${size} rounded-full ${className}`} />
);

// Button skeleton
export const ButtonSkeleton = ({ className = "" }) => (
  <Skeleton className={`h-10 w-full rounded-lg ${className}`} />
);

// Table row skeleton
export const TableRowSkeleton = ({ columns = 4 }) => (
  <div className="flex items-center space-x-4 p-4 border-b border-gray-100">
    {[...Array(columns)].map((_, i) => (
      <Skeleton
        key={i}
        className={`h-4 bg-gradient-to-r from-gray-200 to-gray-300 ${
          i === 0 ? "w-1/3" : i === columns - 1 ? "w-20" : "w-1/4"
        }`}
      />
    ))}
  </div>
);

// List item skeleton
export const ListItemSkeleton = () => (
  <div className="flex items-center space-x-3 p-3 border-b border-gray-100">
    <AvatarSkeleton />
    <div className="flex-1 space-y-2">
      <Skeleton className="h-4 w-1/2 bg-gradient-to-r from-gray-200 to-gray-300" />
      <Skeleton className="h-3 w-3/4 bg-gradient-to-r from-gray-200 to-gray-300" />
    </div>
  </div>
);

// Form field skeleton
export const FormFieldSkeleton = ({ label = true }) => (
  <div className="space-y-2">
    {label && (
      <Skeleton className="h-4 w-24 bg-gradient-to-r from-gray-200 to-gray-300" />
    )}
    <Skeleton className="h-10 w-full rounded-lg bg-gradient-to-r from-gray-200 to-gray-300" />
  </div>
);

// Card skeleton
export const CardSkeleton = ({ header = true, footer = false }) => (
  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
    {header && (
      <div className="mb-4">
        <Skeleton className="h-6 w-1/2 mb-2 bg-gradient-to-r from-gray-200 to-gray-300" />
        <Skeleton className="h-4 w-3/4 bg-gradient-to-r from-gray-200 to-gray-300" />
      </div>
    )}

    <div className="space-y-3">
      <Skeleton className="h-4 w-full bg-gradient-to-r from-gray-200 to-gray-300" />
      <Skeleton className="h-4 w-5/6 bg-gradient-to-r from-gray-200 to-gray-300" />
      <Skeleton className="h-4 w-4/5 bg-gradient-to-r from-gray-200 to-gray-300" />
    </div>

    {footer && (
      <div className="mt-4 pt-4 border-t border-gray-100">
        <div className="flex justify-between items-center">
          <Skeleton className="h-4 w-20 bg-gradient-to-r from-gray-200 to-gray-300" />
          <Skeleton className="h-8 w-24 rounded-lg bg-gradient-to-r from-gray-200 to-gray-300" />
        </div>
      </div>
    )}
  </div>
);

// Grid skeleton for multiple items
export const GridSkeleton = ({
  items = 6,
  columns = 3,
  itemComponent = ProductCardSkeleton,
  className = "",
}) => {
  // Limit columns to maximum of 4 for desktop
  const maxColumns = Math.min(columns, 4);

  return (
    <div
      className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-${maxColumns} xl:grid-cols-${maxColumns} gap-6 ${className}`}
    >
      {[...Array(items)].map((_, i) => (
        <itemComponent key={i} />
      ))}
    </div>
  );
};

// Horizontal scroll skeleton
export const HorizontalScrollSkeleton = ({ items = 4 }) => (
  <div className="flex space-x-4 overflow-x-auto pb-4">
    {[...Array(items)].map((_, i) => (
      <div key={i} className="flex-shrink-0 w-64">
        <ProductCardSkeleton />
      </div>
    ))}
  </div>
);

// Dashboard stats skeleton
export const DashboardStatsSkeleton = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
    {[...Array(4)].map((_, i) => (
      <div
        key={i}
        className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
      >
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <Skeleton className="h-4 w-20 bg-gradient-to-r from-gray-200 to-gray-300" />
            <Skeleton className="h-8 w-16 bg-gradient-to-r from-gray-200 to-gray-300" />
          </div>
          <Skeleton className="w-12 h-12 rounded-lg bg-gradient-to-r from-gray-200 to-gray-300" />
        </div>
      </div>
    ))}
  </div>
);

// Sidebar skeleton
export const SidebarSkeleton = () => (
  <div className="w-64 bg-white border-r border-gray-100 p-4">
    <div className="space-y-4">
      {/* Logo skeleton */}
      <Skeleton className="h-8 w-32 bg-gradient-to-r from-gray-200 to-gray-300" />

      {/* Navigation items */}
      {[...Array(6)].map((_, i) => (
        <div key={i} className="flex items-center space-x-3 p-2">
          <Skeleton className="w-5 h-5 bg-gradient-to-r from-gray-200 to-gray-300" />
          <Skeleton className="h-4 w-20 bg-gradient-to-r from-gray-200 to-gray-300" />
        </div>
      ))}
    </div>
  </div>
);

// Main content skeleton
export const MainContentSkeleton = () => (
  <div className="flex-1 p-6 space-y-6">
    {/* Header skeleton */}
    <div className="flex items-center justify-between">
      <Skeleton className="h-8 w-48 bg-gradient-to-r from-gray-200 to-gray-300" />
      <Skeleton className="h-10 w-32 rounded-lg bg-gradient-to-r from-gray-200 to-gray-300" />
    </div>

    {/* Content skeleton */}
    <div className="space-y-6">
      <CardSkeleton header={true} footer={true} />
      <GridSkeleton items={6} columns={4} />
    </div>
  </div>
);

// Full page skeleton
export const PageSkeleton = () => (
  <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
    <div className="flex">
      <SidebarSkeleton />
      <MainContentSkeleton />
    </div>
  </div>
);

// Hero section skeleton
export const SkeletonHero = () => (
  <div className="relative h-96 md:h-[500px] lg:h-[600px] bg-gradient-to-br from-gray-50 via-white to-gray-50 rounded-2xl overflow-hidden">
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="text-center space-y-4">
        <Skeleton className="h-12 w-96 mx-auto bg-gradient-to-r from-gray-200 to-gray-300" />
        <Skeleton className="h-6 w-80 mx-auto bg-gradient-to-r from-gray-200 to-gray-300" />
        <div className="flex justify-center space-x-4">
          <Skeleton className="h-12 w-32 rounded-lg bg-gradient-to-r from-gray-200 to-gray-300" />
          <Skeleton className="h-12 w-32 rounded-lg bg-gradient-to-r from-gray-200 to-gray-300" />
        </div>
      </div>
    </div>
  </div>
);

// Alias exports for backward compatibility
export const SkeletonHorizontalScroll = HorizontalScrollSkeleton;
export const SkeletonProductCard = ProductCardSkeleton;

// Default export for backward compatibility
const SkeletonLoader = {
  ProductCard: ProductCardSkeleton,
  Text: TextSkeleton,
  Image: ImageSkeleton,
  Avatar: AvatarSkeleton,
  Button: ButtonSkeleton,
  TableRow: TableRowSkeleton,
  ListItem: ListItemSkeleton,
  FormField: FormFieldSkeleton,
  Card: CardSkeleton,
  Grid: GridSkeleton,
  HorizontalScroll: HorizontalScrollSkeleton,
  DashboardStats: DashboardStatsSkeleton,
  Sidebar: SidebarSkeleton,
  MainContent: MainContentSkeleton,
  Page: PageSkeleton,
  Skeleton: Skeleton,
  Hero: SkeletonHero,
  SkeletonHero: SkeletonHero,
  SkeletonHorizontalScroll: SkeletonHorizontalScroll,
  SkeletonProductCard: SkeletonProductCard,
};

export default SkeletonLoader;

// Product detail page image skeleton
export const ProductImageSkeleton = () => (
  <div className="aspect-square bg-gray-100 rounded-xl overflow-hidden animate-pulse">
    <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300"></div>
  </div>
);

// Product detail page thumbnail skeleton
export const ProductThumbnailSkeleton = () => (
  <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden animate-pulse">
    <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300"></div>
  </div>
);

// Variant image skeleton
export const VariantImageSkeleton = () => (
  <div className="w-16 h-16 bg-gray-100 rounded overflow-hidden animate-pulse">
    <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300"></div>
  </div>
);

// Mobile variant image skeleton
export const MobileVariantImageSkeleton = () => (
  <div className="w-14 h-14 bg-gray-100 rounded overflow-hidden animate-pulse">
    <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300"></div>
  </div>
);

// Product detail page main content skeleton
export const ProductDetailSkeleton = () => (
  <div className="space-y-6">
    {/* Title skeleton */}
    <div className="space-y-2">
      <div className="h-8 bg-gray-200 rounded w-3/4 animate-pulse"></div>
      <div className="h-6 bg-gray-200 rounded w-1/2 animate-pulse"></div>
    </div>

    {/* Price skeleton */}
    <div className="space-y-2">
      <div className="h-8 bg-gray-200 rounded w-1/3 animate-pulse"></div>
      <div className="h-4 bg-gray-200 rounded w-1/4 animate-pulse"></div>
    </div>

    {/* Stock status skeleton */}
    <div className="flex items-center space-x-2">
      <div className="w-4 h-4 bg-gray-200 rounded animate-pulse"></div>
      <div className="h-4 bg-gray-200 rounded w-32 animate-pulse"></div>
    </div>

    {/* Variant selector skeleton */}
    <div className="space-y-3">
      <div className="h-6 bg-gray-200 rounded w-1/3 animate-pulse"></div>
      <div className="grid grid-cols-6 gap-1">
        {[...Array(6)].map((_, i) => (
          <VariantImageSkeleton key={i} />
        ))}
      </div>
    </div>

    {/* Add to cart skeleton */}
    <div className="space-y-4">
      <div className="h-12 bg-gray-200 rounded animate-pulse"></div>
    </div>
  </div>
);
